import { useQuery } from '@tanstack/react-query'
import type { FunctionComponent } from 'react'
import { api } from '../../../pages/p/[product_slug]'
import { useTranslate } from '../../contexts/TranslationContext'
import type { ProductEvaluationMinimizedResult } from '../../data/ProductEvaluationFragment'
import { useActiveDog } from '../../user/ActiveDogContext'
import { InfoBar } from './InfoBar'
import style from './InfoBarGroup.module.sass'

type InfoBarGroupProps = {
	isFedProduct: boolean
	productPlacedTopThree: boolean
	fedProductIsProductsIncluded: boolean
	placeOfProduct: number | boolean | undefined
	productEvaluation: ProductEvaluationMinimizedResult | undefined
	productsTotal: number | undefined
	isVeterinaryDiet: boolean
}

export const InfoBarGroup: FunctionComponent<InfoBarGroupProps> = ({
	isFedProduct,
	productPlacedTopThree,
	fedProductIsProductsIncluded,
	placeOfProduct,
	productsTotal,
	productEvaluation,
	isVeterinaryDiet,
}) => {
	const translate = useTranslate()

	const { dog: dogInfo } = useActiveDog()

	const suitableCategory = useQuery({
		queryKey: ['getSuitableCategory', dogInfo],
		queryFn: async () => {
			return dogInfo
				? api('getSuitableCategory', {
						dogInfo,
					})
				: null
		},

		enabled: Boolean(dogInfo),
	})

	const generalElementInfoContained =
		productEvaluation?.dsmalo ||
		productEvaluation?.dsprilis ||
		productEvaluation?.dsneuvedeno ||
		productEvaluation?.dsok

	const detailedElementInfoContained =
		productEvaluation?.d95malo ||
		productEvaluation?.d95prilis ||
		productEvaluation?.d95ok ||
		productEvaluation?.d110malo ||
		productEvaluation?.d110prilis ||
		productEvaluation?.d110ok ||
		productEvaluation?.sdo14reproMalo ||
		productEvaluation?.sdo14reproPrilis ||
		productEvaluation?.sdo14reproOk ||
		productEvaluation?.sdo15malo ||
		productEvaluation?.sdo15prilis ||
		productEvaluation?.sdo15ok ||
		productEvaluation?.s15malo ||
		productEvaluation?.s15prilis ||
		productEvaluation?.s15ok

	const d95 = suitableCategory.data?.remoteId.includes('95'.toLowerCase())
	const d110 = suitableCategory.data?.remoteId.includes('110'.toLowerCase())
	const s15 = suitableCategory.data?.remoteId.includes('puppy'.toLowerCase())
	const sdo14repro = suitableCategory.data?.remoteId.includes('newborn'.toLowerCase())

	return (
		<div className={style.infoBarGroup}>
			{isFedProduct && (
				<>
					{fedProductIsProductsIncluded ? (
						<>
							{productPlacedTopThree ? (
								<InfoBar type="positiveGreen" label={translate('results.message.topthree')} />
							) : (
								<InfoBar
									type="negativeRed"
									label={`${translate('results.message.placed.first')}  ${translate(
										'results.message.placed.second',
									)} ${Number(placeOfProduct)}. ${translate('results.message.placed.third')}`}
								/>
							)}
						</>
					) : (
						<InfoBar
							type="negativeRed"
							label={`${translate('results.message.preferable.first')} ${Number(
								productsTotal,
							)} ${translate('results.message.preferable.second')}`}
						/>
					)}
				</>
			)}
			{productPlacedTopThree ? (
				<>
					{generalElementInfoContained && productEvaluation?.dsok && (
						<InfoBar type="positiveGreen" label={productEvaluation.dsok} />
					)}
					{detailedElementInfoContained && sdo14repro && productEvaluation?.sdo14reproOk && (
						<InfoBar type="positiveGreen" label={productEvaluation.sdo14reproOk} />
					)}
					{detailedElementInfoContained && s15 && productEvaluation?.s15ok && (
						<InfoBar type="positiveGreen" label={productEvaluation.s15ok} />
					)}

					{detailedElementInfoContained && d110 && productEvaluation?.d110ok && (
						<InfoBar type="positiveGreen" label={productEvaluation.d110ok} />
					)}

					{detailedElementInfoContained && d95 && productEvaluation?.d95ok && (
						<InfoBar type="positiveGreen" label={productEvaluation.d95ok} />
					)}
					{productEvaluation?.dsneuvedeno && <InfoBar type="infoYellow" label={productEvaluation.dsneuvedeno} />}
					{isVeterinaryDiet && <InfoBar type="infoYellow" label={translate('results.message.veterinaryDiet')} />}
					{productEvaluation?.dsmalo && <InfoBar type="negativeRed" label={productEvaluation.dsmalo} />}
					{productEvaluation?.dsprilis && <InfoBar type="negativeRed" label={productEvaluation.dsprilis} />}
					{detailedElementInfoContained && sdo14repro && (
						<>
							{productEvaluation?.sdo14reproMalo && (
								<InfoBar type="negativeRed" label={productEvaluation.sdo14reproMalo} />
							)}

							{productEvaluation?.sdo14reproPrilis && (
								<InfoBar type="negativeRed" label={productEvaluation.sdo14reproPrilis} />
							)}
						</>
					)}
					{detailedElementInfoContained && s15 && (
						<>
							{productEvaluation?.s15malo && <InfoBar type="negativeRed" label={productEvaluation.s15malo} />}
							{productEvaluation?.s15prilis && <InfoBar type="negativeRed" label={productEvaluation.s15prilis} />}
						</>
					)}

					{detailedElementInfoContained && d110 && (
						<>
							{productEvaluation?.d110malo && <InfoBar type="negativeRed" label={productEvaluation.d110malo} />}
							{productEvaluation?.d110prilis && <InfoBar type="negativeRed" label={productEvaluation.d110prilis} />}
						</>
					)}
					{detailedElementInfoContained && d95 && (
						<>
							{productEvaluation?.d95malo && <InfoBar type="negativeRed" label={productEvaluation.d95malo} />}
							{productEvaluation?.d95prilis && <InfoBar type="negativeRed" label={productEvaluation.d95prilis} />}
						</>
					)}
				</>
			) : (
				<>
					{generalElementInfoContained && (
						<>
							{productEvaluation?.dsmalo && <InfoBar type="negativeRed" label={productEvaluation.dsmalo} />}
							{productEvaluation?.dsprilis && <InfoBar type="negativeRed" label={productEvaluation.dsprilis} />}
						</>
					)}
					{detailedElementInfoContained && sdo14repro && (
						<>
							{productEvaluation?.sdo14reproMalo && (
								<InfoBar type="negativeRed" label={productEvaluation.sdo14reproMalo} />
							)}
							{productEvaluation?.sdo14reproPrilis && (
								<InfoBar type="negativeRed" label={productEvaluation.sdo14reproPrilis} />
							)}
						</>
					)}

					{detailedElementInfoContained && s15 && (
						<>
							{productEvaluation?.s15malo && <InfoBar type="negativeRed" label={productEvaluation.s15malo} />}
							{productEvaluation?.s15prilis && <InfoBar type="negativeRed" label={productEvaluation.s15prilis} />}
						</>
					)}
					{detailedElementInfoContained && d110 && (
						<>
							{productEvaluation?.d110malo && <InfoBar type="negativeRed" label={productEvaluation.d110malo} />}
							{productEvaluation?.d110prilis && <InfoBar type="negativeRed" label={productEvaluation.d110prilis} />}
						</>
					)}
					{detailedElementInfoContained && d95 && (
						<>
							{productEvaluation?.d95malo && <InfoBar type="negativeRed" label={productEvaluation.d95malo} />}
							{productEvaluation?.d95prilis && <InfoBar type="negativeRed" label={productEvaluation.d95prilis} />}
						</>
					)}
					{productEvaluation?.dsneuvedeno && <InfoBar type="infoYellow" label={productEvaluation.dsneuvedeno} />}
					{isVeterinaryDiet && <InfoBar type="infoYellow" label={translate('results.message.veterinaryDiet')} />}
					{generalElementInfoContained && productEvaluation?.dsok && (
						<InfoBar type="positiveGreen" label={productEvaluation.dsok} />
					)}
					{detailedElementInfoContained && sdo14repro && productEvaluation?.sdo14reproOk && (
						<InfoBar type="positiveGreen" label={productEvaluation.sdo14reproOk} />
					)}
					{detailedElementInfoContained && s15 && productEvaluation?.s15ok && (
						<InfoBar type="positiveGreen" label={productEvaluation.s15ok} />
					)}

					{detailedElementInfoContained && d110 && productEvaluation?.d110ok && (
						<InfoBar type="positiveGreen" label={productEvaluation.d110ok} />
					)}

					{detailedElementInfoContained && d95 && productEvaluation?.d95ok && (
						<InfoBar type="positiveGreen" label={productEvaluation.d95ok} />
					)}
				</>
			)}
		</div>
	)
}
